import Downshift from 'downshift'
import debounce from 'lodash.debounce'
import { useCallback, useEffect, useState } from 'react'

import config from 'config'

interface Option {
  value: number
  label: string
}

interface Props {
  label: string
  value: number | ''
  onChange: (value: number | '') => any
  onSearch: (search: string) => Promise<Option[]>
}

// Autocomplete assíncrono genérico: busca no backend (via query-runner "search")
// conforme o usuário digita, com debounce - mesmo padrão de Items/Devices/components/Autocomplete.tsx
export function AutocompleteSelect({
  label,
  value,
  onChange,
  onSearch
}: Props) {
  const [isLoading, setIsLoading] = useState(false)
  const [searchValue, setSearchValue] = useState('')
  const [options, setOptions] = useState<Option[]>([])
  const [selected, setSelected] = useState<Option>()

  useEffect(() => {
    if (value === '') setSelected(undefined)
  }, [value])

  useEffect(() => {
    if (searchValue === '') return

    search(searchValue)
  }, [searchValue])

  const onClick = (option: Option) => {
    setSelected(option)
    setSearchValue('')
    setOptions([])
    onChange(option.value)
  }

  const search = useCallback(
    debounce(async (search: string): Promise<void> => {
      try {
        if (search.length < config.min_search_length) return

        setIsLoading(true)

        const results = await onSearch(search)

        setOptions(results)
        setIsLoading(false)
      } catch (err: any) {
        setIsLoading(false)
        console.error(err)
      }
    }, config.autocomplete_debounce_rate),
    [onSearch]
  )

  return (
    <Downshift
      onChange={(selection) => (selection ? onClick(selection) : undefined)}
      itemToString={(item) => item?.label ?? ''}
      selectedItem={selected ?? null}
    >
      {({ getInputProps, getItemProps, isOpen }) => (
        <div className="relative">
          <label className="block text-left text-sm font-medium text-gray-700">
            {label}
          </label>
          <div className="flex items-center">
            <input
              className="mt-1 block w-full rounded-md shadow-sm py-2 px-3 border border-gray-300 focus:outline-none focus:border-emerald-500"
              {...getInputProps({
                value: selected ? selected.label : searchValue,
                onChange: (e: any) => {
                  setSelected(undefined)
                  setSearchValue(e.currentTarget.value)
                  if (e.currentTarget.value === '') onChange('')
                }
              })}
            />
            {isLoading && (
              <div className="absolute right-1">
                <svg
                  className="animate-spin -ml-1 mr-1 mt-2 h-5 w-5 text-emerald-700"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
              </div>
            )}
          </div>
          {!selected && isOpen && (
            <ul className="absolute mt-2 rounded-md bg-white z-20 w-full shadow flex flex-col max-h-60 overflow-auto">
              {options.length > 0 ? (
                options.map((option) => (
                  <li
                    key={option.value}
                    className="px-4 py-2 text-sm w-full cursor-pointer bg-white hover:bg-gray-100"
                    {...getItemProps({ item: option })}
                  >
                    {option.label}
                  </li>
                ))
              ) : (
                <li className="px-4 py-2 text-sm w-full text-gray-500">
                  Nenhum resultado encontrado
                </li>
              )}
            </ul>
          )}
        </div>
      )}
    </Downshift>
  )
}
